import { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
  InstagramIcon,
  MenuIcon,
  MailIcon,
  PackageIcon,
  SparklesIcon,
  ShieldCheckIcon,
} from 'lucide-react';

import photo1 from './photos/1.jpeg';
import photo2 from './photos/2.jpeg';
import photo3 from './photos/3.jpeg';
import photo4 from './photos/4.jpeg';
import photo5 from './photos/5.jpeg';
import photo6 from './photos/6.jpeg';
import photo7 from './photos/7.jpeg';

const photos = [photo1, photo2, photo3, photo4, photo5, photo6, photo7];

function SliderPrevArrow({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Previous"
      style={{ display: 'flex' }}
      className="absolute z-10 left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow-md items-center justify-center cursor-pointer transition-all duration-200 bg-white/90 hover:bg-white text-slate-900 dark:bg-slate-800/80 dark:hover:bg-slate-800 dark:text-white"
    >
      <ChevronLeftIcon className="w-5 h-5" />
    </button>
  );
}

function SliderNextArrow({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Next"
      style={{ display: 'flex' }}
      className="absolute z-10 right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow-md items-center justify-center cursor-pointer transition-all duration-200 bg-white/90 hover:bg-white text-slate-900 dark:bg-slate-800/80 dark:hover:bg-slate-800 dark:text-white"
    >
      <ChevronRightIcon className="w-5 h-5" />
    </button>
  );
}

const navLinks = [
  { id: 'examples', label: 'Examples' },
  { id: 'order', label: 'Order' },
  { id: 'faq', label: 'FAQ' },
];

const faqs = [
  {
    question: 'How does it work once I get it?',
    answer:
      'The box comes with a premium Olight (included) that charges via USB. Double-press the light to turn it on or off. You can also control it using the Olight app.',
  },
  {
    question: 'What are the dimensions?',
    answer: 'Approximately 150mm × 150mm × 150mm (5.9 × 5.9 × 5.9 inches).',
  },
  {
    question: 'How durable is it?',
    answer:
      'Built to last. Made from high-quality PLA plastic — durable, eco-friendly, and lightweight.',
  },
  {
    question: 'How many photos can I include?',
    answer: 'Up to 4 photos per order, one on each side of the box.',
  },
  {
    question: 'What file formats are supported?',
    answer: 'JPEG, PNG, and WebP all work great.',
  },
  {
    question: 'How long does processing take?',
    answer: 'Orders are processed and shipped within 1–2 business days.',
  },
];

function App() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Lightbox: lock scroll, restore focus, close on Escape
  useEffect(() => {
    if (!selectedImage) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      lastFocusedRef.current?.focus?.();
    };
  }, [selectedImage]);

  // Close mobile nav on Escape
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const goTo = (id: string) => {
    setMobileNavOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-40 backdrop-blur bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/70 dark:border-slate-800/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a
            href="/"
            className="text-lg sm:text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-brand-600 to-purple-500 dark:from-brand-400 dark:to-purple-400"
          >
            lightyour.space
          </a>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => goTo(link.id)}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            <a
              href="https://instagram.com/lightyour.space"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Light Your Space on Instagram"
              className="p-2 rounded-md text-slate-600 hover:text-pink-600 dark:text-slate-300 dark:hover:text-pink-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
            >
              <InstagramIcon className="h-5 w-5" aria-hidden="true" />
            </a>
            <button
              type="button"
              className="md:hidden p-2 rounded-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60"
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav"
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileNavOpen((v) => !v)}
            >
              {mobileNavOpen ? (
                <XIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <MenuIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileNavOpen && (
          <nav
            id="mobile-nav"
            aria-label="Mobile"
            className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"
          >
            <ul className="px-4 py-2 space-y-1">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => goTo(link.id)}
                    className="w-full text-left px-3 py-3 rounded-md text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="main" className="pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50 via-white to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-950"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 -z-10 h-72 w-72 sm:h-96 sm:w-96 rounded-full bg-brand-300/40 dark:bg-brand-500/20 blur-3xl"
          />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center animate-fade-in-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900/60">
              <span aria-hidden="true">🇺🇸</span> Designed &amp; made in the USA
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Light up your space with{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-600 to-purple-500 dark:from-brand-400 dark:to-purple-400">
                your favorite photos
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl leading-relaxed text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Send us up to four photos and we'll turn them into a glowing,
              hand-finished keepsake — perfect for your home or as a gift for
              someone you care about.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => goTo('order')}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold shadow-sm transition-colors"
              >
                Start your order
              </button>
              <button
                onClick={() => goTo('examples')}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white hover:bg-slate-50 text-slate-900 font-semibold ring-1 ring-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-white dark:ring-slate-700 transition-colors"
              >
                See examples
              </button>
            </div>
          </div>
        </section>

        {/* Features */}
        <section aria-label="Why a custom light box" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <ul className="grid gap-4 sm:grid-cols-3">
            {[
              {
                Icon: SparklesIcon,
                title: 'Hand-finished',
                body: 'Every box is printed and assembled with care, one at a time.',
              },
              {
                Icon: PackageIcon,
                title: 'Olight included',
                body: 'Ships with a rechargeable Olight — USB-C, app-controlled, ready to glow.',
              },
              {
                Icon: ShieldCheckIcon,
                title: 'Built to last',
                body: 'Durable, eco-friendly PLA. Light, sturdy, and meant to be displayed.',
              },
            ].map(({ Icon, title, body }) => (
              <li
                key={title}
                className="rounded-xl p-5 bg-white ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-slate-800"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
                </div>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Examples */}
        <section id="examples" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Examples
              </h2>
              <p className="mt-3 text-slate-600 dark:text-slate-300">
                A few favorites from past orders. Tap any image to enlarge.
              </p>
            </div>

            <Slider
              dots
              infinite
              speed={500}
              slidesToShow={3}
              slidesToScroll={1}
              prevArrow={<SliderPrevArrow />}
              nextArrow={<SliderNextArrow />}
              responsive={[
                { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
                { breakpoint: 640, settings: { slidesToShow: 1, slidesToScroll: 1 } },
              ]}
              className="gallery-slider"
            >
              {photos.map((photo, index) => (
                <div key={index} className="px-2">
                  <button
                    type="button"
                    onClick={() => setSelectedImage(photo)}
                    className="group block w-full overflow-hidden rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-100 dark:bg-slate-900"
                    aria-label={`Open example ${index + 1}`}
                  >
                    <img
                      src={photo}
                      alt={`Custom photo light box example ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </button>
                </div>
              ))}
            </Slider>
          </div>

          {selectedImage && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Enlarged example"
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
              onClick={() => setSelectedImage(null)}
            >
              <button
                type="button"
                aria-label="Close"
                className="absolute top-4 right-4 p-2 rounded-md text-white/90 hover:text-white hover:bg-white/10"
                onClick={() => setSelectedImage(null)}
              >
                <XIcon className="h-7 w-7" aria-hidden="true" />
              </button>
              <img
                src={selectedImage}
                alt="Enlarged example"
                className="max-h-[90vh] max-w-full w-auto h-auto rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </section>

        {/* Order */}
        <section id="order" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Order a custom light box
              </h2>
              <p className="mt-3 text-slate-600 dark:text-slate-300">
                A simple two-step process. We'll handle the rest.
              </p>
            </div>

            <ol className="space-y-4">
              <li className="rounded-xl p-6 bg-white ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-slate-800">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white text-sm font-bold">
                    1
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <MailIcon className="h-4 w-4 text-brand-600 dark:text-brand-400" aria-hidden="true" />
                      Email your photos &amp; address
                    </h3>
                    <p className="mt-2 text-slate-600 dark:text-slate-300">
                      Send to{' '}
                      <a
                        href="mailto:orders@lightyour.space"
                        className="font-medium text-brand-700 dark:text-brand-400 underline underline-offset-2 hover:text-brand-800 dark:hover:text-brand-300"
                      >
                        orders@lightyour.space
                      </a>
                      :
                    </p>
                    <ul className="mt-2 list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-300">
                      <li>Up to 4 photos of your choice</li>
                      <li>Your shipping address</li>
                    </ul>
                  </div>
                </div>
              </li>

              <li className="rounded-xl p-6 bg-white ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-slate-800">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white text-sm font-bold">
                    2
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Send payment via Venmo</h3>
                    <p className="mt-2 text-slate-600 dark:text-slate-300">
                      Pay{' '}
                      <span className="font-medium text-slate-900 dark:text-white">@maxslink</span>:
                    </p>
                    <ul className="mt-2 list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-300">
                      <li>$50 — custom light box</li>
                      <li>$10 — shipping &amp; handling</li>
                    </ul>
                  </div>
                </div>
              </li>
            </ol>

            <div className="mt-6 rounded-xl p-5 bg-brand-50 ring-1 ring-brand-100 dark:bg-brand-500/5 dark:ring-brand-500/20">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">After your order:</span>{' '}
                we'll send a confirmation design for approval, then ship with tracking once it's ready.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Frequently asked questions
              </h2>
            </div>
            <ul className="space-y-3">
              {faqs.map((faq, index) => {
                const open = expandedFaq === index;
                const panelId = `faq-panel-${index}`;
                const buttonId = `faq-button-${index}`;
                return (
                  <li
                    key={index}
                    className="rounded-xl bg-white ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-slate-800 overflow-hidden"
                  >
                    <h3>
                      <button
                        id={buttonId}
                        aria-expanded={open}
                        aria-controls={panelId}
                        className="w-full px-5 sm:px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        onClick={() => setExpandedFaq(open ? null : index)}
                      >
                        <span className="font-medium text-slate-900 dark:text-white">
                          {faq.question}
                        </span>
                        <ChevronDownIcon
                          className={`h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400 transition-transform ${
                            open ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      hidden={!open}
                      className="px-5 sm:px-6 pb-5 text-slate-600 dark:text-slate-300"
                    >
                      <p>{faq.answer}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
          <p>© {new Date().getFullYear()} lightyour.space — designed &amp; made in the USA</p>
          <div className="flex items-center gap-4">
            <a
              href="mailto:orders@lightyour.space"
              className="hover:text-slate-900 dark:hover:text-white"
            >
              orders@lightyour.space
            </a>
            <a
              href="https://instagram.com/lightyour.space"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-pink-600 dark:hover:text-pink-400"
            >
              <InstagramIcon className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
